import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Loader2, CheckCircle, Calendar, Landmark, Copy, CheckCheck } from 'lucide-react'

export interface JoiningFee {
  amount: number
  currency: string
  description?: string
}

export interface BankDetails {
  accountName?: string
  sortCode?: string
  accountNumber?: string
  iban?: string
  bic?: string
  reference?: string
  notes?: string
}

export interface BracketOption {
  bracketId: string
  name: string
}

interface RegisterInterestDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  seasonName: string
  seasonStartDate: string
  seasonEndDate: string
  leagueName?: string
  joiningFee?: JoiningFee | null
  termsAndConditions?: string | null
  bankDetails?: BankDetails | null
  showBankDetails?: boolean
  /** Override payment reference for this season (uses bankDetails.reference if not set) */
  paymentReference?: string | null
  brackets: BracketOption[]
  onSubmit: (bracketId: string) => Promise<void>
  submitting?: boolean
  error?: string | null
}

function formatDate(dateString: string): string {
  return new Date(dateString + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatCurrency(amountInSmallestUnit: number, currency: string): string {
  const amount = amountInSmallestUnit / 100
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

export function RegisterInterestDialog({
  open,
  onOpenChange,
  seasonName,
  seasonStartDate,
  seasonEndDate,
  leagueName,
  joiningFee,
  termsAndConditions,
  bankDetails,
  showBankDetails,
  paymentReference,
  brackets,
  onSubmit,
  submitting = false,
  error,
}: RegisterInterestDialogProps) {
  // Use season override if set, otherwise fall back to group default
  const effectiveReference = paymentReference || bankDetails?.reference
  const [selectedBracketId, setSelectedBracketId] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Pre-select first bracket when dialog opens or brackets change
  useEffect(() => {
    if (open && brackets.length > 0 && !selectedBracketId) {
      setSelectedBracketId(brackets[0].bracketId)
    }
  }, [open, brackets, selectedBracketId])

  // Reset selection when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedBracketId(null)
    }
  }, [open])

  const handleSubmit = async () => {
    if (!selectedBracketId) return
    await onSubmit(selectedBracketId)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Register for {seasonName}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {leagueName && <span>{leagueName}</span>}
            {leagueName && <span>•</span>}
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(seasonStartDate)} – {formatDate(seasonEndDate)}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Fee info */}
          {joiningFee && (
            <div className="rounded-lg border p-3 bg-muted/30">
              <p className="text-sm font-medium">Joining Fee</p>
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(joiningFee.amount, joiningFee.currency)}
              </p>
              {joiningFee.description && (
                <p className="text-sm text-muted-foreground">{joiningFee.description}</p>
              )}
            </div>
          )}

          {/* Bank details - shown when fee exists and showBankDetails is enabled */}
          {joiningFee && showBankDetails && bankDetails && (
            <div className="rounded-lg border p-3 bg-blue-50/50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-3">
                <Landmark className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Payment Details</p>
              </div>
              <div className="space-y-2 text-sm">
                {bankDetails.accountName && (
                  <BankDetailRow 
                    label="Account Name" 
                    value={bankDetails.accountName} 
                    field="accountName"
                    copiedField={copiedField}
                    onCopy={setCopiedField}
                  />
                )}
                {bankDetails.sortCode && (
                  <BankDetailRow 
                    label="Sort Code" 
                    value={bankDetails.sortCode} 
                    field="sortCode"
                    copiedField={copiedField}
                    onCopy={setCopiedField}
                  />
                )}
                {bankDetails.accountNumber && (
                  <BankDetailRow 
                    label="Account Number" 
                    value={bankDetails.accountNumber} 
                    field="accountNumber"
                    copiedField={copiedField}
                    onCopy={setCopiedField}
                  />
                )}
                {bankDetails.iban && (
                  <BankDetailRow 
                    label="IBAN" 
                    value={bankDetails.iban} 
                    field="iban"
                    copiedField={copiedField}
                    onCopy={setCopiedField}
                  />
                )}
                {bankDetails.bic && (
                  <BankDetailRow 
                    label="BIC/SWIFT" 
                    value={bankDetails.bic} 
                    field="bic"
                    copiedField={copiedField}
                    onCopy={setCopiedField}
                  />
                )}
                {effectiveReference && (
                  <BankDetailRow 
                    label="Reference" 
                    value={effectiveReference} 
                    field="reference"
                    copiedField={copiedField}
                    onCopy={setCopiedField}
                  />
                )}
              </div>
              {bankDetails.notes && (
                <p className="mt-3 text-xs text-blue-700 dark:text-blue-300 bg-blue-100/50 dark:bg-blue-900/30 p-2 rounded">
                  {bankDetails.notes}
                </p>
              )}
            </div>
          )}

          {/* Bracket selection */}
          {brackets.length > 0 && (
            <div className="space-y-2">
              <Label>Select Your Preferred Bracket</Label>
              <p className="text-xs text-muted-foreground mb-2">
                The organizer may assign you to a different bracket based on availability
              </p>
              <div className="space-y-2">
                {brackets.map(bracket => (
                  <button
                    key={bracket.bracketId}
                    onClick={() => setSelectedBracketId(bracket.bracketId)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      selectedBracketId === bracket.bracketId
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <span className="font-medium">{bracket.name}</span>
                    {selectedBracketId === bracket.bracketId && (
                      <CheckCircle className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Terms and conditions */}
          {termsAndConditions && (
            <div className="space-y-2">
              <Label>Terms & Conditions</Label>
              <div className="rounded-lg border p-3 bg-muted/30 max-h-32 overflow-y-auto">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {termsAndConditions}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                By registering, you agree to these terms
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedBracketId}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Register Interest
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Helper component for bank detail rows with copy functionality
function BankDetailRow({ 
  label, 
  value, 
  field,
  copiedField,
  onCopy,
}: { 
  label: string
  value: string 
  field: string
  copiedField: string | null
  onCopy: (field: string | null) => void
}) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      onCopy(field)
      setTimeout(() => onCopy(null), 1500)
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = value
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      onCopy(field)
      setTimeout(() => onCopy(null), 1500)
    }
  }

  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <span className="font-mono text-foreground">{value}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded transition-colors"
          title={`Copy ${label.toLowerCase()}`}
        >
          {copiedField === field ? (
            <CheckCheck className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3 text-muted-foreground" />
          )}
        </button>
      </div>
    </div>
  )
}
